import { useEffect, useMemo, useState } from "react";
import { II } from "../../components/common/Icons";
import HrModal from "./HrModal";
import api from "../../services/api";
import "../../assets/styles/HR/HrPayrollPg.css";

const fmtDate = (d) => {
  try { return d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "—"; }
  catch { return "—"; }
};

const TEMPLATE_ROWS = [
  ["S NO","Emp Code","Emp Name","Designation","Dept (Team)","Payable days","Absent / LOP","Net paid days",
   "Gross Pay (Actuals)","CTC (Actuals)","Basic Salary","HRA","Bonus","Other Allowance","Stipend",
   "Reimbursement","Other Earnings","Incentive","Gross Earning","PF","ESI","Profession Tax","Advance","TDS",
   "Gross Deduction","Pension","EPF","Total Employee PF","Total Employee ESI","Gratuity","Total CTC","Month","Year"],
  ["1","EMP-2024-1456","Arun Kumar","Software Engineer","Engineering","30","1","29",
   "65000","80000","30000","12000","4000","3000","0",
   "0","2500","2000","53500","1800","500","200","0","1000",
   "3500","0","1800","1800","500","1000","80000","February","2026"],
];

export default function HrPayrollPg() {
  const [logs,     setLogs]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [suc,      setSuc]      = useState("");
  const [err,      setErr]      = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [processResult, setProcessResult] = useState(null);

  const [form, setForm] = useState({
    title: "", month: "", year: "", comment: "", file: null
  });

  const flash = (msg, isErr = false) => {
    if (isErr) { setErr(msg); setTimeout(()=>setErr(""),4500); }
    else       { setSuc(msg); setTimeout(()=>setSuc(""),4500); }
  };

  const load = async () => {
    try {
      const logs = await api.getPayrollProcessLogs().catch(()=>[]);
      setLogs(Array.isArray(logs) ? logs : []);
    } catch {}
  };
  useEffect(()=>{ load(); },[]);

  /* ── Download template ── */
  const downloadTemplate = () => {
    const csv = TEMPLATE_ROWS.map(r=>r.join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "payroll-upload-template.csv";
    a.click(); URL.revokeObjectURL(a.href);
  };

  /* ── Direct upload + process ── */
  const handleUpload = async () => {
    if (!form.file) return flash("Please select a payroll CSV file", true);
    if (!form.file.name.toLowerCase().endsWith(".csv")) return flash("Only CSV files supported. Export Excel as CSV first.", true);
    setLoading(true);
    setProcessResult(null);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      if (form.month)   fd.append("month", form.month);
      if (form.year)    fd.append("year",  form.year);
      if (form.title)   fd.append("title", form.title);
      if (form.comment) fd.append("comment", form.comment);

      const res = await api.processPayrollUpload(fd);
      setProcessResult(res);
      setForm({ title:"", month:"", year:"", comment:"", file:null });
      setShowUpload(false);
      await load();
      const updatedCount = Array.isArray(res.updated) ? res.updated.length : (res.updated_count ?? res.updated ?? 0);
      const skippedCount = Array.isArray(res.skipped) ? res.skipped.length : (res.skipped_count ?? res.skipped ?? 0);
      flash(`Payroll processed: ${updatedCount} payslips generated, ${skippedCount} skipped.`);
    } catch (e) {
      flash(e.message || "Upload failed", true);
    }
    setLoading(false);
  };

  /* ── Download run log PDF ── */
  const downloadLogCsv = (log) => {
    const skipped = Array.isArray(log?.skipped) ? log.skipped : [];
    const rows = [
      ["Run ID", log.run_id||"—"],
      ["Month/Year", `${log.month_label||"—"} / ${log.year_value||"—"}`],
      ["Source File", log.source_file_name||"—"],
      ["Payslips Updated", log.updated_count||0],
      ["Skipped", log.skipped_count||0],
      ["Processed By", log.processed_by_name||"—"],
      ["Date", fmtDate(log.created_at)],
      [],
      ["Skipped Employees"],
      ["Emp Code","Reason"],
      ...skipped.map(s=>[s.emp_code||"—", s.reason||"Unknown"])
    ];
    const csv = rows.map(r=>r.join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `payroll_log_${log.run_id}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const currentYear = new Date().getFullYear();
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  return (
    <div>
      <div className="sec">Payroll Management</div>

      {suc && <div className="toast" style={{background:"#eff6ff",border:"1px solid #bfdbfe",color:"#1e40af"}}>{suc}</div>}
      {err && <div className="toast hrpayrollpg-inline-1">{err}</div>}

      {/* Process result banner */}
      {processResult && (
        <div style={{background:"#f0fdf4",border:"2px solid #bbf7d0",borderRadius:12,padding:"16px 20px",marginBottom:16,display:"flex",gap:20,alignItems:"center"}}>
          
          <div>
            <div style={{fontWeight:700,fontSize:15,color:"#15803d"}}>Payroll Processed Successfully</div>
            <div style={{fontSize:13,color:"#166534",marginTop:4}}>
              <b>{Array.isArray(processResult.skipped) ? processResult.skipped.length : (processResult.skipped_count ?? processResult.skipped ?? 0)}</b> payslips generated &nbsp;·&nbsp;
              <b>{Array.isArray(processResult.updated) ? processResult.updated.length : (processResult.updated_count ?? processResult.updated ?? 0)}</b> skipped &nbsp;·&nbsp;
              Employees can now download payslips from their portal
            </div>
          </div>
          <button onClick={()=>setProcessResult(null)}
            style={{marginLeft:"auto",background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#94a3b8"}}>✕</button>
        </div>
      )}

      {/* Step 1 — Template */}
      <div className="cd mb16">
        <div className="cd-h">
          <div className="cd-t">Step 1 — Download Payroll Template</div>
          <button className="btn-o btn-s" onClick={downloadTemplate}>{II("dwn",12)} Download CSV Template</button>
        </div>
        <div className="cd-b">
          <div style={{fontSize:13,color:"#64748b",lineHeight:1.7}}>
            Fill in employee payroll data using the CSV template. Required columns:
            <br/>
            <span style={{fontFamily:"monospace",fontSize:11,background:"#f1f5f9",padding:"2px 6px",borderRadius:4}}>
              Emp Code, Basic Salary, HRA, Bonus, Gross Earning, PF, ESI, Gross Deduction, Month, Year
            </span>
            <br/>
            <b style={{color:"#dc2626"}}>Excel (.xlsx) not supported</b> — Export as CSV before uploading.
          </div>
        </div>
      </div>

      {/* Step 2 — Direct Upload */}
      <div className="cd mb16">
        <div className="cd-h">
          <div className="cd-t">Step 2 — Upload & Process Payroll</div>
          <div className="hrpayrollpg-inline-4">
            <button className="btn btn-s" onClick={()=>setShowUpload(true)}>
              {II("send",12)} Upload Payroll CSV
            </button>
          </div>
        </div>
        
      </div>

      {/* Step 3 — Process Logs */}
      <div className="cd">
        <div className="cd-h">
          <div className="cd-t">Payroll Process Logs</div>
          <div className="cd-s">{logs.length} runs</div>
        </div>
        <div className="cd-b hrpayrollpg-inline-10">
          {logs.length === 0 ? (
            <div style={{textAlign:"center",padding:30,color:"#94a3b8",fontSize:13}}>No payroll runs yet. Upload a CSV to get started.</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Run ID</th><th>Source File</th><th>Month/Year</th>
                  <th>Updated</th><th>Skipped</th><th>Processed By</th><th>Date</th><th>Log</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.run_id}>
                    <td className="hrpayrollpg-inline-12">{l.run_id}</td>
                    <td style={{fontSize:12}}>{l.source_file_name||"—"}</td>
                    <td style={{fontSize:12}}>{l.month_label||"—"} {l.year_value||""}</td>
                    <td>
                      <span style={{fontWeight:700,color:"#16a34a"}}>{l.updated_count||0}</span>
                    </td>
                    <td>
                      <span style={{fontWeight:700,color:l.skipped_count>0?"#d97706":"#94a3b8"}}>{l.skipped_count||0}</span>
                    </td>
                    <td style={{fontSize:12}}>{l.processed_by_name||"—"}</td>
                    <td style={{fontSize:11,color:"#94a3b8"}}>{fmtDate(l.created_at)}</td>
                    <td>
                      <button className="btn-o btn-s" onClick={()=>downloadLogCsv(l)}>
                        {II("dwn",11)} CSV
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <HrModal open={showUpload} onClose={()=>setShowUpload(false)} title="Upload Payroll CSV">
        <div style={{marginBottom:14,background:"#eff6ff",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#2563eb"}}>
          Payslips will be generated <b>immediately</b> after upload. No PM approval required.
        </div>

        <div className="fg">
          <label className="fl">Payroll File (CSV only) *</label>
          <input type="file" accept=".csv,.txt" className="fi"
            onChange={e=>setForm(p=>({...p, file:e.target.files?.[0]||null}))} />
          {form.file && !form.file.name.toLowerCase().endsWith(".csv") && (
            <div style={{color:"#dc2626",fontSize:11,marginTop:4}}>Please upload a CSV file only</div>
          )}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div className="fg">
            <label className="fl">Month (optional)</label>
            <select className="fi" value={form.month} onChange={e=>setForm(p=>({...p,month:e.target.value}))}>
              <option value="">Auto-detect from CSV</option>
              {MONTHS.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="fg">
            <label className="fl">Year (optional)</label>
            <select className="fi" value={form.year} onChange={e=>setForm(p=>({...p,year:e.target.value}))}>
              <option value="">Auto-detect from CSV</option>
              {[currentYear, currentYear-1, currentYear-2].map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div className="fg">
          <label className="fl">Title (optional)</label>
          <input className="fi" placeholder="e.g. March 2026 Payroll"
            value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} />
        </div>

        <div className="fg">
          <label className="fl">Notes (optional)</label>
          <textarea className="fta" rows={2} placeholder="Any notes about this payroll run…"
            value={form.comment} onChange={e=>setForm(p=>({...p,comment:e.target.value}))} />
        </div>

        <div className="hrpayrollpg-inline-13">
          <button className="btn-o" onClick={()=>setShowUpload(false)}>Cancel</button>
          <button className="btn" onClick={handleUpload}
            disabled={loading || !form.file}>
            {loading ? "Processing…" : "Upload & Generate Payslips"}
          </button>
        </div>
      </HrModal>
    </div>
  );
}
